import { Link } from 'react-router-dom'

interface FooterColumn {
  title: string
  links: { label: string; href: string; external?: boolean }[]
}

interface SiteFooterProps {
  columns?: FooterColumn[]
}

const DEFAULT_COLUMNS: FooterColumn[] = [
  {
    title: 'Solutions',
    links: [
      { label: 'Financial Services', href: '/solutions' },
      { label: 'Healthcare', href: '/solutions' },
      { label: 'Manufacturing', href: '/solutions' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Governance', href: '/governance' },
      { label: 'Investors', href: '/investors' },
      { label: 'Contact', href: '/contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Cookie Policy', href: '/cookies' },
    ],
  },
]

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

export default function SiteFooter({ columns = DEFAULT_COLUMNS }: SiteFooterProps) {
  return (
    <footer className="border-t border-white/[0.08] bg-black/20 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand column */}
          <div>
            <span
              className="text-lg text-white/80 tracking-[0.3em] block mb-4"
              style={brandFont}
            >
              LUKH&Lambda;S
            </span>
            <p
              className="text-sm text-white/40 leading-relaxed"
              style={bodyFont}
            >
              Guardian of ethical AI consciousness
            </p>
          </div>

          {/* Dynamic columns */}
          {columns.map((col) => (
            <div key={col.title}>
              <h3
                className="text-xs text-white/50 tracking-[0.15em] uppercase mb-4"
                style={bodyFont}
              >
                {col.title}
              </h3>
              <ul className="space-y-2">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-white/40 hover:text-white/70 transition-colors"
                        style={bodyFont}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-white/40 hover:text-white/70 transition-colors"
                        style={bodyFont}
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/30" style={bodyFont}>
            &copy; {new Date().getFullYear()} LUKHAS AI. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a
              href="https://lukhas.ai"
              className="text-xs text-white/30 hover:text-white/50 transition-colors"
              style={bodyFont}
            >
              lukhas.ai
            </a>
            <a
              href="https://lukhas.dev"
              className="text-xs text-white/30 hover:text-white/50 transition-colors"
              style={bodyFont}
            >
              lukhas.dev
            </a>
            <a
              href="https://lukhas.id"
              className="text-xs text-white/30 hover:text-white/50 transition-colors"
              style={bodyFont}
            >
              lukhas.id
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
